import { nip19 } from 'nostr-tools';
import { ExternalLinkIcon, MoreHorizontalIcon, TrashIcon, CopyIcon, GitForkIcon, PencilIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDeleteEvent } from '@/hooks/useDeleteEvent';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useToast } from '@/hooks/useToast';
import { getKindConfig, SET_LIST_KINDS } from '@/lib/nip51';
import type { LinkList } from '@/hooks/useLinkLists';

interface LinkListCardProps {
  list: LinkList;
  isOwner: boolean;
}

function itemPreview(list: LinkList): string {
  const item = list.items[0];
  if (!item) return '';
  if (item.tag === 'r') return item.label || item.value;
  if (item.tag === 'p') return item.value.length > 16 ? item.value.slice(0, 8) + '…' + item.value.slice(-4) : item.value;
  if (item.tag === 't') return '#' + item.value;
  if (item.tag === 'relay') return item.value;
  return item.value.length > 30 ? item.value.slice(0, 30) + '…' : item.value;
}

export function LinkListCard({ list, isOwner }: LinkListCardProps) {
  const { deleteEvent } = useDeleteEvent();
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const navigate = useNavigate();

  const kindConfig = getKindConfig(list.kind);
  const isSet = SET_LIST_KINDS.includes(list.kind);

  // Only sets can have naddr links; standard lists link to the profile
  const listHref = isSet
    ? `/${nip19.naddrEncode({ kind: list.kind, pubkey: list.event.pubkey, identifier: list.dTag })}`
    : null;

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this list?')) return;
    try {
      await deleteEvent(list.event);
      toast({ title: 'List deleted', description: 'Your list has been deleted.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to delete list.', variant: 'destructive' });
    }
  };

  const handleFork = () => {
    if (!user) {
      toast({ title: 'Sign in required', description: 'You need to sign in to fork a list.' });
      return;
    }
    navigate('/create', { state: { forkFrom: list } });
  };

  const handleCopyLink = () => {
    const url = listHref ? `${window.location.origin}${listHref}` : window.location.href;
    navigator.clipboard.writeText(url);
    toast({ title: 'Link copied', description: 'Shareable link copied to clipboard.' });
  };

  const editHref = isSet
    ? `/edit/${list.dTag}?kind=${list.kind}`
    : `/edit/${list.kind}?kind=${list.kind}`;

  return (
    <Card className="group transition-all hover:shadow-md">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {/* Kind badge */}
            {kindConfig && (
              <span className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <span>{kindConfig.icon}</span>
                <span>{kindConfig.name}</span>
              </span>
            )}

            {listHref ? (
              <Link to={listHref} className="block">
                <CardTitle className="text-base group-hover:text-primary transition-colors truncate">
                  {list.title || 'Untitled List'}
                </CardTitle>
              </Link>
            ) : (
              <CardTitle className="text-base truncate">
                {list.title || kindConfig?.name || 'List'}
              </CardTitle>
            )}

            {list.description && (
              <CardDescription className="mt-1 line-clamp-2">
                {list.description}
              </CardDescription>
            )}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="size-8 p-0 shrink-0">
                <MoreHorizontalIcon className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {listHref && (
                <DropdownMenuItem onClick={handleCopyLink}>
                  <CopyIcon />
                  Copy Link
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleFork}>
                <GitForkIcon />
                Fork List
              </DropdownMenuItem>
              {isOwner && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={editHref}>
                      <PencilIcon />
                      Edit
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem variant="destructive" onClick={handleDelete}>
                    <TrashIcon />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {/* Preview first 3 items */}
        <div className="space-y-1.5 mt-2">
          {list.items.slice(0, 3).map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
              {item.tag === 'r' ? (
                <a
                  href={item.value}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-foreground transition-colors truncate"
                >
                  <ExternalLinkIcon className="size-3.5 shrink-0" />
                  <span className="truncate">{item.label || item.value}</span>
                </a>
              ) : (
                <span className="truncate">{itemPreview({ ...list, items: [item] })}</span>
              )}
            </div>
          ))}
          {list.items.length > 3 && listHref && (
            <Link to={listHref} className="text-xs text-primary hover:underline">
              +{list.items.length - 3} more items
            </Link>
          )}
          {list.items.length === 0 && (
            <span className="text-xs text-muted-foreground italic">No items</span>
          )}
        </div>

        {/* Hashtags */}
        {list.hashtags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {list.hashtags.map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs">
                #{tag}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
